import { z } from 'zod';
import * as dt from './data.types.js';
import { PeripheralIdentityModeSchema } from './peripheral-profile-identity.js';

const integerIdSchema = z.number().int().positive();

export const containerBarcodeSchema = z.string().trim().min(1).max(64);

export const efemLoadPortBindingStatusSchema = z.nativeEnum(dt.EFEM_LOAD_PORT_BINDING_STATUS);
export const efemLoadPortBindingSourceSchema = z.nativeEnum(dt.EFEM_LOAD_PORT_BINDING_SOURCE);

const scanContainerTelemetryBaseSchema = z.object({
  rawBarcode: z.string().min(1),
  scannedAt: z.string(),
  timestamp: z.string().optional(),
  identityMode: PeripheralIdentityModeSchema.optional(),
  serialNumber: z.string().min(1).optional(),
  gatewaySerialNumber: z.string().min(1).optional(),
  deviceCategory: z.enum(['barcode-scanner', 'handheld-barcode-scanner']).optional(),
  profileRef: z.string().min(1).optional(),
});

export const ScanContainerTelemetrySchema = scanContainerTelemetryBaseSchema.superRefine((val, ctx) => {
  const mode = val.identityMode ?? (val.serialNumber ? 'edge-guard' : undefined);
  if (mode === 'cloud-resolve') {
    if (!val.gatewaySerialNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'gatewaySerialNumber is required when identityMode is cloud-resolve',
        path: ['gatewaySerialNumber'],
      });
    }
    if (!val.deviceCategory) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'deviceCategory is required when identityMode is cloud-resolve',
        path: ['deviceCategory'],
      });
    }
    if (!val.profileRef) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'profileRef is required when identityMode is cloud-resolve',
        path: ['profileRef'],
      });
    }
    return;
  }
  if (!val.serialNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'serialNumber is required for edge-guard scan_container telemetry',
      path: ['serialNumber'],
    });
  }
});


export const LoadPortSnapshotEntrySchema = z.object({
  id: z.number().int().positive(),
  status: z.nativeEnum(dt.LOAD_PORT_STATUS),
});

export const LoadPortsSnapshotTelemetrySchema = z.object({
  gatewaySerialNumber: z.string().min(1).optional(),
  sampledAt: z.string(),
  loadPorts: z.array(LoadPortSnapshotEntrySchema).min(1),
});


export const loadPortsSnapshotTelemetryEventSchema = z.object({
  kind: z.literal('loadports_snapshot'),
  tenantId: z.string().uuid(),
  topic: z.string().min(1),
  gatewaySerialNumber: z.string().min(1),
  sampledAt: z.string(),
  loadPorts: z.array(LoadPortSnapshotEntrySchema).min(1),
});


/** IoT Rule → SQS envelope for scan_container, scan_container_error, and loadports_snapshot. */
const scanContainerBindingEventSchema = z.object({
  kind: z.literal('scan_container'),
  tenantId: z.string().uuid(),
  topic: z.string().min(1),
  rawBarcode: z.string().min(1),
  scannedAt: z.string(),
  timestamp: z.string().optional(),
  identityMode: PeripheralIdentityModeSchema.optional(),
  serialNumber: z.string().min(1).optional(),
  gatewaySerialNumber: z.string().min(1).optional(),
  deviceCategory: z.enum(['barcode-scanner', 'handheld-barcode-scanner']).optional(),
  profileRef: z.string().min(1).optional(),
}).superRefine((val, ctx) => {
  const mode = val.identityMode ?? (val.serialNumber ? 'edge-guard' : undefined);
  if (mode === 'cloud-resolve') {
    if (!val.gatewaySerialNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'gatewaySerialNumber is required for cloud-resolve scan_container',
        path: ['gatewaySerialNumber'],
      });
    }
    if (!val.deviceCategory) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'deviceCategory is required for cloud-resolve scan_container',
        path: ['deviceCategory'],
      });
    }
    if (!val.profileRef) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'profileRef is required for cloud-resolve scan_container',
        path: ['profileRef'],
      });
    }
    return;
  }
  if (!val.serialNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'serialNumber is required for edge-guard scan_container',
      path: ['serialNumber'],
    });
  }
});

export const efemBindingTelemetryEventSchema = z.union([
  scanContainerBindingEventSchema,
  z.object({
    kind: z.literal('scan_container_error'),
    tenantId: z.string().uuid(),
    topic: z.string().min(1),
    serialNumber: z.string().min(1),
    rawBarcode: z.string().optional(),
    error: z.string(),
    timestamp: z.string(),
  }),
  loadPortsSnapshotTelemetryEventSchema,
]);


/** Reconcile tuning for sustained EMPTY → auto-clear (cloud defaults). */
export const EFEM_BINDING_RECONCILE_DEFAULTS = {
  snapshotIntervalSec: 15,
  emptyStreakThreshold: 3,
} as const;

export const ScanContainerErrorTelemetrySchema = z.object({
  serialNumber: z.string().min(1),
  rawBarcode: z.string().optional(),
  error: z.string(),
  timestamp: z.string(),
});

export const EfemLoadPortBindingViewSchema = z.object({
  loadPortId: z.number().int().positive(),
  containerId: integerIdSchema.nullable(),
  containerName: z.string().nullable().optional(),
  containerBarcode: z.string().nullable().optional(),
  rawBarcode: z.string().nullable(),
  normalizedBarcode: z.string().nullable(),
  scannerSerialNumber: z.string().nullable(),
  scannerDisplayLabel: z.string().nullable().optional(),
  resolutionStatus: efemLoadPortBindingStatusSchema,
  source: efemLoadPortBindingSourceSchema,
  conflictLoadPortId: z.number().int().positive().nullable().optional(),
  scannedAt: z.string().nullable(),
  boundAt: z.string().nullable(),
  clearedAt: z.string().nullable().optional(),
});


export const LoadPortWithBindingSchema = z.object({
  id: integerIdSchema,
  name: z.string(),
  status: z.nativeEnum(dt.LOAD_PORT_STATUS),
  size: z.nativeEnum(dt.EFEM_LOAD_PORT_SIZE),
  containerType: z.string().optional(),
  containerId: z.string().optional(),
  binding: EfemLoadPortBindingViewSchema.nullable().optional(),
});

export const LoadPortsWithBindingsResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    loadPorts: z.array(LoadPortWithBindingSchema),
  }),
});

export const PatchEfemLoadPortBindingSchema = z
  .object({
    containerId: integerIdSchema.optional(),
    clear: z.boolean().optional(),
    forceRebind: z.boolean().optional(),
  })
  .refine((v) => v.clear === true || v.containerId !== undefined, {
    message: 'Provide containerId or clear: true',
  });

export const PostScanContainerRequestSchema = z.object({
  loadPortId: z.number().int().positive(),
  timeoutMs: z.number().int().positive().max(30000).optional(),
});

export const PostScanContainerResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    scannerSerialNumber: z.string(),
    binding: EfemLoadPortBindingViewSchema.nullable(),
  }),
});

export const EfemLoadPortBindingResponseSchema = z.object({
  success: z.literal(true),
  data: EfemLoadPortBindingViewSchema,
});

export const EfemTransferVerifyScanSchema = z.object({
  slotNumber: z.number().int().positive(),
  fingerprint: z.string(),
  imageId: z.number().int().positive().optional(),
});

export const EfemTransferVerifyRequestSchema = z.object({
  sourceScans: z.array(EfemTransferVerifyScanSchema),
  targetScans: z.array(EfemTransferVerifyScanSchema),
  transferId: z.string().optional(),
  notes: z.string().optional(),
});

export const EfemTransferVerifyResultSchema = z.object({
  verified: z.boolean(),
  matches: z.array(
    z.object({
      fingerprint: z.string(),
      sourceSlot: z.number().int(),
      targetSlot: z.number().int(),
      verified: z.boolean(),
    }),
  ),
  missingInTarget: z.array(
    z.object({
      fingerprint: z.string(),
      sourceSlot: z.number().int(),
    }),
  ),
  extraInTarget: z.array(
    z.object({
      fingerprint: z.string(),
      targetSlot: z.number().int(),
    }),
  ),
  discrepancies: z.array(
    z.object({
      type: z.string(),
      message: z.string(),
    }),
  ),
});

export const EfemTransferVerifyResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: EfemTransferVerifyResultSchema,
});
// Inferred types
export type EfemBindingTelemetryEvent = z.infer<typeof efemBindingTelemetryEventSchema>;
export type EfemLoadPortBindingResponse = z.infer<typeof EfemLoadPortBindingResponseSchema>;
export type EfemLoadPortBindingView = z.infer<typeof EfemLoadPortBindingViewSchema>;
export type EfemTransferVerifyRequest = z.infer<typeof EfemTransferVerifyRequestSchema>;
export type EfemTransferVerifyResponse = z.infer<typeof EfemTransferVerifyResponseSchema>;
export type EfemTransferVerifyResult = z.infer<typeof EfemTransferVerifyResultSchema>;
export type EfemTransferVerifyScan = z.infer<typeof EfemTransferVerifyScanSchema>;
export type LoadPortSnapshotEntry = z.infer<typeof LoadPortSnapshotEntrySchema>;
export type LoadPortWithBinding = z.infer<typeof LoadPortWithBindingSchema>;
export type LoadPortsSnapshotTelemetry = z.infer<typeof LoadPortsSnapshotTelemetrySchema>;
export type LoadPortsSnapshotTelemetryEvent = z.infer<typeof loadPortsSnapshotTelemetryEventSchema>;
export type LoadPortsWithBindingsResponse = z.infer<typeof LoadPortsWithBindingsResponseSchema>;
export type PatchEfemLoadPortBinding = z.infer<typeof PatchEfemLoadPortBindingSchema>;
export type PostScanContainerRequest = z.infer<typeof PostScanContainerRequestSchema>;
export type PostScanContainerResponse = z.infer<typeof PostScanContainerResponseSchema>;
export type ScanContainerErrorTelemetry = z.infer<typeof ScanContainerErrorTelemetrySchema>;
export type ScanContainerTelemetry = z.infer<typeof ScanContainerTelemetrySchema>;
